import { trackDownload } from '../utils/downloadIndicator';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  IconButton,
  useMediaQuery,
  useTheme,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Autocomplete,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { 
  Edit as EditIcon,
  GetApp as GetAppIcon,
  Notes as NotesIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { projectsApi, purchaseOrdersApi, tenantApi } from '../services/api';
import VendorSelector from '../components/VendorSelector';
import SavedItemsSearch from '../components/SavedItemsSearch';

// Sortable Table Row Component
function SortableTableRow({ id, children, isDragging, dragHandleProps, isEditingAllowed, ...props }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style} {...props}>
      {React.Children.map(children, (child, index) => {
        // Pass drag props to the first cell (drag handle) if editing is allowed
        if (index === 0 && React.isValidElement(child) && isEditingAllowed) {
          return React.cloneElement(child, {
            ...child.props,
            children: React.cloneElement(child.props.children, {
              ...attributes,
              ...listeners,
            })
          });
        }
        return child;
      })}
    </TableRow>
  );
}

function PurchaseOrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [reverting, setReverting] = useState(false);
  
  // Edit mode states
  const [editMode, setEditMode] = useState(false);
  const [editingPO, setEditingPO] = useState(null);
  const [editingItems, setEditingItems] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Notes dialog states
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  
  // Add item states
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState({ name: '', quantity: 1, price: 0, units: 'Each' });
  const [savedItemsSearch, setSavedItemsSearch] = useState('');
  
  // Drag and drop states
  const [draggedItem, setDraggedItem] = useState(null);
  const [reorderLoading, setReorderLoading] = useState(false);

  // Departments state
  const [departments, setDepartments] = useState(['General']);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // VAT rate (20% for UK)
  const VAT_RATE = 0.20;

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  // Calculate total
  const calculateTotal = (items) => {
    if (!items || !items.length) return 0;
    return items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
  };

  // Calculate NETT total (before VAT)
  const calculateNettTotal = (items) => {
    return calculateTotal(items);
  };

  // Calculate Gross total (including VAT)
  const calculateGrossTotal = (items) => {
    const nettTotal = calculateNettTotal(items);
    return nettTotal * (1 + VAT_RATE);
  };

  const getProjectLabel = (project) => {
    const projectNumber = project.project_no || project.project_id;
    const name = project.organisation_name || project.project_organisation_name || project.quote_reference;
    return `Project #${projectNumber}${name ? ` - ${name}` : ''}`;
  };

  // Edit mode handlers
  const handleEnterEditMode = () => {
    setEditMode(true);
    setEditingPO({
      vendor_id: purchaseOrder.vendor_id,
      vendor_name: purchaseOrder.vendor_name,
      delivery_address_line1: purchaseOrder.delivery_address_line1 || '',
      delivery_address_line2: purchaseOrder.delivery_address_line2 || '',
      delivery_city: purchaseOrder.delivery_city || '',
      delivery_postcode: purchaseOrder.delivery_postcode || '',
      notes: purchaseOrder.notes || '',
      reference: purchaseOrder.reference || '',
      due_date: purchaseOrder.due_date ? purchaseOrder.due_date.split('T')[0] : '',
      contact_name: purchaseOrder.contact_name || '',
      contact_number: purchaseOrder.contact_number || '',
      project_id: purchaseOrder.project_id || null,
      department: purchaseOrder.department || departments[0] || '',
    });
    setEditingItems([...purchaseOrder.items]);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditingPO(null);
    setEditingItems([]);
    setShowAddItem(false);
    setNewItem({ name: '', quantity: 1, price: 0, units: 'Each' });
    setSavedItemsSearch('');
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      
      // Update purchase order details
      const poUpdateData = {
        vendor_id: editingPO.vendor_id,
        delivery_address_line1: editingPO.delivery_address_line1,
        delivery_address_line2: editingPO.delivery_address_line2,
        delivery_city: editingPO.delivery_city,
        delivery_postcode: editingPO.delivery_postcode,
        notes: editingPO.notes,
        reference: editingPO.reference,
        due_date: editingPO.due_date || null,
        contact_name: editingPO.contact_name,
        contact_number: editingPO.contact_number,
        project_id: editingPO.project_id || null,
        department: editingPO.department,
      };
      
      await purchaseOrdersApi.update(orderId, poUpdateData);
      
      // Update items if they've changed
      for (const item of editingItems) {
        if (item.purchase_order_item_id) {
          // Update existing item
          await purchaseOrdersApi.updateItem(item.purchase_order_item_id, {
            name: item.name,
            quantity: item.quantity,
            price: item.price,
            units: item.units,
          });
        } else {
          // Add new item
          await purchaseOrdersApi.addItem(orderId, {
            name: item.name,
            quantity: item.quantity,
            price: item.price,
            units: item.units,
          });
        }
      }
      
      // Refresh the purchase order
      await fetchPurchaseOrder();
      setEditMode(false);
      setEditingPO(null);
      setEditingItems([]);
      setShowAddItem(false);
      setNewItem({ name: '', quantity: 1, price: 0, units: 'Each' });
      
    } catch (error) {
      console.error('Error saving changes:', error);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Item handlers
  const handleItemChange = (index, field, value) => {
    const newItems = [...editingItems];
    
    // Ensure price is always a valid number
    if (field === 'price') {
      value = isNaN(value) || value === '' ? 0 : Number(value);
    }
    
    newItems[index] = { ...newItems[index], [field]: value };
    setEditingItems(newItems);
  };

  const handleDeleteItem = async (itemId, index) => {
    if (itemId) {
      try {
        await purchaseOrdersApi.deleteItem(itemId);
        await fetchPurchaseOrder(); // Refresh data
      } catch (error) {
        console.error('Error deleting item:', error);
        setError('Failed to delete item. Please try again.');
        return;
      }
    }
    // Remove from editing items
    const newItems = editingItems.filter((_, i) => i !== index);
    setEditingItems(newItems);
  };

  const handleAddItem = () => {
    if (!newItem.name.trim()) return;
    
    const itemToAdd = {
      ...newItem,
      purchase_order_item_id: null, // Mark as new
      order_index: editingItems.length,
    };
    
    setEditingItems([...editingItems, itemToAdd]);
    setNewItem({ name: '', quantity: 1, price: 0, units: 'Each' });
    setSavedItemsSearch('');
    setShowAddItem(false);
  };

  const handleSavedItemSelect = (savedItem) => {
    setNewItem({
      name: savedItem.name,
      quantity: 1,
      price: 0,
      units: savedItem.units || 'Each'
    });
    setSavedItemsSearch('');
  };

  // Drag and drop handlers
  const handleDragStart = (event) => {
    const { active } = event;
    const draggedItem = editingItems.find(item => item.purchase_order_item_id === active.id);
    setDraggedItem(draggedItem);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setDraggedItem(null);

    if (!over || active.id === over.id) {
      return;
    }

    const activeIndex = editingItems.findIndex(item => item.purchase_order_item_id === active.id);
    const overIndex = editingItems.findIndex(item => item.purchase_order_item_id === over.id);

    if (activeIndex !== overIndex) {
      try {
        setReorderLoading(true);
        
        // Create new order array
        const newItems = arrayMove(editingItems, activeIndex, overIndex);
        setEditingItems(newItems);
        
        // Create order update payload
        const itemOrders = newItems
          .filter(item => item.purchase_order_item_id) // Only existing items
          .map((item, index) => ({
            item_id: item.purchase_order_item_id,
            order_index: index + 1
          }));

        // Update order in backend if we have existing items
        if (itemOrders.length > 0) {
          await purchaseOrdersApi.bulkReorderItems(orderId, { item_orders: itemOrders });
        }
        
      } catch (error) {
        console.error('Error reordering items:', error);
        setError('Failed to reorder items. Please refresh the page.');
      } finally {
        setReorderLoading(false);
      }
    }
  };

  // Handle PDF export
  const handleExportPDF = trackDownload(async () => {
    try {
      setExporting(true);
      const response = await purchaseOrdersApi.exportPDF(orderId);
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Try to get filename from headers (prefer X-Filename), fallback to parsing Content-Disposition
      const headerFilename = response.headers['x-filename'];
      let filename = headerFilename || `PO${orderId}.pdf`;
      if (!headerFilename) {
        const cd = response.headers['content-disposition'];
        if (cd) {
          const fnStar = cd.match(/filename\*=UTF-8''([^;\n]+)/i);
          const fnBasic = cd.match(/filename="?([^";\n]+)"?/i);
          if (fnStar && fnStar[1]) {
            try { filename = decodeURIComponent(fnStar[1]); } catch { filename = fnStar[1]; }
          } else if (fnBasic && fnBasic[1]) {
            filename = fnBasic[1];
          }
        }
      }
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setError('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  });

  // Handle marking purchase order as complete
  const handleMarkAsComplete = async () => {
    try {
      setCompleting(true);
      await purchaseOrdersApi.markAsComplete(orderId);
      // Refresh the purchase order data
      const response = await purchaseOrdersApi.getById(orderId, true);
      setPurchaseOrder(response.data);
    } catch (error) {
      console.error('Error marking purchase order as complete:', error);
      setError('Failed to mark purchase order as complete. Please try again.');
    } finally {
      setCompleting(false);
    }
  };

  // Handle reverting purchase order back to New
  const handleRevertToNew = async () => {
    try {
      setReverting(true);
      await purchaseOrdersApi.revertToNew(orderId);
      // Refresh the purchase order data
      const response = await purchaseOrdersApi.getById(orderId, true);
      setPurchaseOrder(response.data);
    } catch (error) {
      console.error('Error reverting purchase order to new:', error);
      setError('Failed to revert purchase order status. Please try again.');
    } finally {
      setReverting(false);
    }
  };

  // Handle notes dialog
  const handleOpenNotesDialog = () => {
    setNotes(purchaseOrder?.notes || '');
    setNotesDialogOpen(true);
  };

  const handleCloseNotesDialog = () => {
    setNotesDialogOpen(false);
    setNotes('');
  };

  const handleSaveNotes = async () => {
    try {
      setSavingNotes(true);
      await purchaseOrdersApi.update(orderId, { notes });
      
      // Update the local state
      setPurchaseOrder(prev => ({
        ...prev,
        notes
      }));
      
      setNotesDialogOpen(false);
      setError(null);
    } catch (error) {
      console.error('Error saving notes:', error);
      setError('Failed to save notes. Please try again.');
    } finally {
      setSavingNotes(false);
    }
  };

  const fetchPurchaseOrder = useCallback(async () => {
    try {
      setLoading(true);
      const response = await purchaseOrdersApi.getById(orderId, true);
      setPurchaseOrder(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching purchase order:', err);
      setError('Failed to load purchase order details.');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (orderId) {
      fetchPurchaseOrder();
    }
  }, [orderId, fetchPurchaseOrder]);

  // Fetch departments on mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await tenantApi.getBranding();
        const depts = response.data?.branding?.departments;
        if (Array.isArray(depts) && depts.length > 0) {
          setDepartments(depts);
        }
      } catch (err) {
        console.error('Error fetching departments:', err);
      }
    };
    fetchDepartments();
  }, []);

  // Fetch projects so an existing purchase order can be linked or moved while editing.
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setProjectsLoading(true);
        const response = await projectsApi.getAll(null, true);
        setProjects(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Error fetching projects for purchase order:', err);
      } finally {
        setProjectsLoading(false);
      }
    };
    fetchProjects();
  }, []);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error && !purchaseOrder) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error" variant="h6">
            {error || 'Purchase order not found'}
          </Typography>
          <Button 
            onClick={() => navigate('/purchase-orders')} 
            sx={{ mt: 2 }}
            variant="contained"
          >
            Back to Purchase Orders
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, px: { xs: 1, sm: 2, md: 3 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {reorderLoading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              Updating item order...
            </Box>
          </Alert>
        )}

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h4" component="h1" sx={{ flexGrow: 1, minWidth: 'fit-content' }}>
            Purchase Order #{purchaseOrder?.purchase_order_no || purchaseOrder?.purchase_order_id}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {!editMode ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleEnterEditMode}
                  size={isMobile ? 'small' : 'medium'}
                >
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<NotesIcon />}
                  onClick={handleOpenNotesDialog}
                  size={isMobile ? 'small' : 'medium'}
                >
                  Notes
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<GetAppIcon />}
                  onClick={handleExportPDF}
                  disabled={exporting}
                  size={isMobile ? 'small' : 'medium'}
                >
                  {exporting ? 'Exporting...' : 'Export PDF'}
                </Button>
                {purchaseOrder?.status === 'Completed' ? (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleRevertToNew}
                    disabled={reverting}
                    size={isMobile ? 'small' : 'medium'}
                  >
                    {reverting ? 'Reverting...' : 'Revert to New'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleMarkAsComplete}
                    disabled={completing}
                    size={isMobile ? 'small' : 'medium'}
                  >
                    {completing ? 'Completing...' : 'Mark as Complete'}
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveChanges}
                  disabled={saving}
                  size={isMobile ? 'small' : 'medium'}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelEdit}
                  disabled={saving}
                  size={isMobile ? 'small' : 'medium'}
                >
                  Cancel
                </Button>
              </>
            )}
            {purchaseOrder?.project_id ? (
              <Chip label="Project-linked" color="primary" variant="outlined" />
            ) : (
              <Chip label="Standalone" color="secondary" variant="outlined" />
            )}
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Purchase Order Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Purchase Order Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      PO Number:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      PO-{purchaseOrder?.purchase_order_no || purchaseOrder?.purchase_order_id}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Created Date:
                    </Typography>
                    <Typography variant="body1">
                      {purchaseOrder?.created_date 
                        ? format(new Date(purchaseOrder.created_date), 'dd MMM yyyy')
                        : 'N/A'
                      }
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Created By:
                    </Typography>
                    <Typography variant="body1">
                      {purchaseOrder?.created_by_name || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Status:
                    </Typography>
                    <Chip 
                      label={purchaseOrder?.status || 'New'} 
                      color={purchaseOrder?.status === 'Completed' ? 'success' : 'default'}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Vendor:
                    </Typography>
                    {editMode ? (
                      <VendorSelector
                        selectedVendor={editingPO?.vendor_id ? { vendor_id: editingPO.vendor_id, name: editingPO.vendor_name } : null}
                        onVendorChange={(vendor) => setEditingPO(prev => ({ 
                          ...prev, 
                          vendor_id: vendor?.vendor_id || null,
                          vendor_name: vendor?.name || ''
                        }))}
                      />
                    ) : (
                      <Typography variant="body1" fontWeight="bold">
                        {purchaseOrder?.vendor_name || 'N/A'}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Reference:
                    </Typography>
                    {editMode ? (
                      <TextField
                        value={editingPO?.reference || ''}
                        onChange={(e) => setEditingPO(prev => ({ ...prev, reference: e.target.value }))}
                        size="small"
                        fullWidth
                      />
                    ) : (
                      <Typography variant="body1">
                        {purchaseOrder?.reference || 'N/A'}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Due Date:
                    </Typography>
                    {editMode ? (
                      <TextField
                        type="date"
                        value={editingPO?.due_date || ''}
                        onChange={(e) => setEditingPO(prev => ({ ...prev, due_date: e.target.value }))}
                        size="small"
                        fullWidth
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    ) : (
                      <Typography variant="body1">
                        {purchaseOrder?.due_date 
                          ? format(new Date(purchaseOrder.due_date), 'dd MMM yyyy')
                          : 'N/A'
                        }
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Project:
                    </Typography>
                    {editMode ? (
                      <Autocomplete
                        options={projects}
                        value={
                          projects.find((project) => project.project_id === editingPO?.project_id) ||
                          (editingPO?.project_id
                            ? {
                                project_id: editingPO.project_id,
                                project_no: purchaseOrder?.project_no,
                                project_organisation_name: purchaseOrder?.project_organisation_name,
                              }
                            : null)
                        }
                        onChange={(event, project) => setEditingPO(prev => ({
                          ...prev,
                          project_id: project?.project_id || null,
                          department: project?.department || prev.department,
                        }))}
                        getOptionLabel={getProjectLabel}
                        isOptionEqualToValue={(option, value) => option.project_id === value.project_id}
                        loading={projectsLoading}
                        disabled={saving}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="Select a project"
                            helperText={editingPO?.project_id ? 'Department is inherited from the selected project.' : 'Leave empty for a standalone purchase order.'}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {projectsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    ) : purchaseOrder?.project_id ? (
                      <Button
                        size="small"
                        onClick={() => navigate(`/projects/${purchaseOrder.project_id}`)}
                        sx={{ justifyContent: 'flex-start', px: 0 }}
                      >
                        {getProjectLabel(purchaseOrder)}
                      </Button>
                    ) : (
                      <Typography variant="body1">Standalone</Typography>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Department:
                    </Typography>
                    {editMode ? (
                      <FormControl size="small" fullWidth disabled={Boolean(editingPO?.project_id)}>
                        <InputLabel>Department</InputLabel>
                        <Select
                          value={editingPO?.department || departments[0] || ''}
                          onChange={(e) => setEditingPO(prev => ({ ...prev, department: e.target.value }))}
                          label="Department"
                        >
                          {departments.map((dept) => (
                            <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Typography variant="body1">
                        {purchaseOrder?.department || 'N/A'}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Total Value
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1">
                          NETT Total:
                        </Typography>
                        <Typography variant="h5" color="primary">
                          {formatCurrency(calculateNettTotal(editMode ? editingItems : purchaseOrder?.items))}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1">
                          Gross Total (inc. VAT):
                        </Typography>
                        <Typography variant="h5" color="primary" fontWeight="bold">
                          {formatCurrency(calculateGrossTotal(editMode ? editingItems : purchaseOrder?.items))}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Delivery Address */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Delivery Address
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Address Line 1:
                    </Typography>
                    {editMode ? (
                      <TextField
                        value={editingPO?.delivery_address_line1 || ''}
                        onChange={(e) => setEditingPO(prev => ({ ...prev, delivery_address_line1: e.target.value }))}
                        size="small"
                        fullWidth
                      />
                    ) : (
                      <Typography variant="body1">
                        {purchaseOrder?.delivery_address_line1 || 'N/A'}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Address Line 2:
                    </Typography>
                    {editMode ? (
                      <TextField
                        value={editingPO?.delivery_address_line2 || ''}
                        onChange={(e) => setEditingPO(prev => ({ ...prev, delivery_address_line2: e.target.value }))}
                        size="small"
                        fullWidth
                      />
                    ) : (
                      <Typography variant="body1">
                        {purchaseOrder?.delivery_address_line2 || 'N/A'}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      City:
                    </Typography>
                    {editMode ? (
                      <TextField
                        value={editingPO?.delivery_city || ''}
                        onChange={(e) => setEditingPO(prev => ({ ...prev, delivery_city: e.target.value }))}
                        size="small"
                        fullWidth
                      />
                    ) : (
                      <Typography variant="body1">
                        {purchaseOrder?.delivery_city || 'N/A'}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Postcode:
                    </Typography>
                    {editMode ? (
                      <TextField
                        value={editingPO?.delivery_postcode || ''}
                        onChange={(e) => setEditingPO(prev => ({ ...prev, delivery_postcode: e.target.value }))}
                        size="small"
                        fullWidth
                      />
                    ) : (
                      <Typography variant="body1">
                        {purchaseOrder?.delivery_postcode || 'N/A'}
                      </Typography>
                    )}
                  </Grid>
                  
                  {/* Contact Information */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Delivery Contact
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Contact Name:
                    </Typography>
                    {editMode ? (
                      <TextField
                        value={editingPO?.contact_name || ''}
                        onChange={(e) => setEditingPO(prev => ({ ...prev, contact_name: e.target.value }))}
                        size="small"
                        fullWidth
                      />
                    ) : (
                      <Typography variant="body1">
                        {purchaseOrder?.contact_name || 'N/A'}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Contact Number:
                    </Typography>
                    {editMode ? (
                      <TextField
                        value={editingPO?.contact_number || ''}
                        onChange={(e) => setEditingPO(prev => ({ ...prev, contact_number: e.target.value }))}
                        size="small"
                        fullWidth
                      />
                    ) : (
                      <Typography variant="body1">
                        {purchaseOrder?.contact_number || 'N/A'}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Items */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Items ({(editMode ? editingItems : purchaseOrder?.items)?.length || 0})
                  </Typography>
                  {editMode && (
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setShowAddItem(true)}
                      size="small"
                    >
                      Add Item
                    </Button>
                  )}
                </Box>

                {/* Add Item Form */}
                {editMode && showAddItem && (
                  <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Add New Item
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12}>
                        <SavedItemsSearch
                          value={savedItemsSearch}
                          onChange={setSavedItemsSearch}
                          onSelect={handleSavedItemSelect}
                          placeholder="Search saved items or type new item name..."
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Item Name"
                          value={newItem.name}
                          onChange={(e) => setNewItem(prev => ({ ...prev, name: e.target.value }))}
                          size="small"
                          fullWidth
                          multiline
                          rows={2}
                        />
                      </Grid>
                      <Grid item xs={4} sm={2}>
                        <TextField
                          label="Quantity"
                          type="number"
                          value={newItem.quantity}
                          onChange={(e) => setNewItem(prev => ({ ...prev, quantity: Number(e.target.value) }))}
                          size="small"
                          fullWidth
                          inputProps={{ min: 1 }}
                        />
                      </Grid>
                      <Grid item xs={4} sm={2}>
                        <TextField
                          label="Units"
                          value={newItem.units}
                          onChange={(e) => setNewItem(prev => ({ ...prev, units: e.target.value }))}
                          size="small"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={4} sm={2}>
                        <TextField
                          label="Price (£)"
                          type="number"
                          value={newItem.price}
                          onChange={(e) => setNewItem(prev => ({ 
                            ...prev, 
                            price: isNaN(Number(e.target.value)) ? 0 : Number(e.target.value)
                          }))}
                          size="small"
                          fullWidth
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            onClick={handleAddItem}
                            disabled={!newItem.name.trim()}
                            size="small"
                          >
                            Add
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={() => setShowAddItem(false)}
                            size="small"
                          >
                            Cancel
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                )}

                {/* Items Table */}
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {editMode && <TableCell width="40px" />}
                        <TableCell>Name</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Units</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell align="right">Total</TableCell>
                        {editMode && <TableCell align="center">Actions</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {editMode ? (
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragStart={handleDragStart}
                          onDragEnd={handleDragEnd}
                        >
                          <SortableContext 
                            items={editingItems.map(item => item.purchase_order_item_id || `new-${editingItems.indexOf(item)}`)} 
                            strategy={verticalListSortingStrategy}
                          >
                            {editingItems.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={7} align="center">
                                  No items added yet
                                </TableCell>
                              </TableRow>
                            ) : (
                              editingItems.map((item, index) => {
                                const itemId = item.purchase_order_item_id || `new-${index}`;
                                return (
                                  <SortableTableRow 
                                    key={itemId} 
                                    id={itemId} 
                                    isDragging={draggedItem?.purchase_order_item_id === item.purchase_order_item_id}
                                    isEditingAllowed={true}
                                  >
                                    <TableCell>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          cursor: 'grab',
                                          '&:active': { cursor: 'grabbing' },
                                        }}
                                      >
                                        <DragIndicatorIcon color="action" />
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        value={item.name}
                                        onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                                        size="small"
                                        fullWidth
                                        multiline
                                        rows={2}
                                      />
                                    </TableCell>
                                    <TableCell align="right">
                                      <TextField
                                        type="number"
                                        value={item.quantity}
                                        onChange={(e) => handleItemChange(index, 'quantity', Number(e.target.value))}
                                        size="small"
                                        inputProps={{ min: 1 }}
                                        sx={{ width: 80 }}
                                      />
                                    </TableCell>
                                    <TableCell align="right">
                                      <TextField
                                        value={item.units || 'Each'}
                                        onChange={(e) => handleItemChange(index, 'units', e.target.value)}
                                        size="small"
                                        sx={{ width: 80 }}
                                      />
                                    </TableCell>
                                    <TableCell align="right">
                                      <TextField
                                        type="number"
                                        value={item.price}
                                        onChange={(e) => {
                                          const value = isNaN(Number(e.target.value)) ? 0 : Number(e.target.value);
                                          handleItemChange(index, 'price', value);
                                        }}
                                        size="small"
                                        inputProps={{ min: 0, step: 0.01 }}
                                        sx={{ width: 100 }}
                                      />
                                    </TableCell>
                                    <TableCell align="right">
                                      {formatCurrency(item.quantity * item.price)}
                                    </TableCell>
                                    <TableCell align="center">
                                      <Tooltip title="Delete">
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => handleDeleteItem(item.purchase_order_item_id, index)}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  </SortableTableRow>
                                );
                              })
                            )}
                          </SortableContext>
                        </DndContext>
                      ) : (
                        purchaseOrder?.items?.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              No items in this purchase order
                            </TableCell>
                          </TableRow>
                        ) : (
                          purchaseOrder?.items?.map((item) => (
                            <TableRow key={item.purchase_order_item_id}>
                              <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {item.name}
                              </TableCell>
                              <TableCell align="right">{item.quantity}</TableCell>
                              <TableCell align="right">{item.units || '-'}</TableCell>
                              <TableCell align="right">{formatCurrency(item.price)}</TableCell>
                              <TableCell align="right">{formatCurrency(item.quantity * item.price)}</TableCell>
                            </TableRow>
                          ))
                        )
                      )}
                      
                      {/* Total Rows */}
                      {(editMode ? editingItems : purchaseOrder?.items)?.length > 0 && (
                        <>
                          <TableRow>
                            <TableCell colSpan={editMode ? 5 : 4} align="right" sx={{ fontWeight: 'bold', borderBottom: 'none' }}>
                              NETT Total:
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', borderBottom: 'none' }}>
                              {formatCurrency(calculateNettTotal(editMode ? editingItems : purchaseOrder?.items))}
                            </TableCell>
                            {editMode && <TableCell sx={{ borderBottom: 'none' }} />}
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={editMode ? 5 : 4} align="right" sx={{ fontWeight: 'bold' }}>
                              Gross Total (inc. VAT):
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              {formatCurrency(calculateGrossTotal(editMode ? editingItems : purchaseOrder?.items))}
                            </TableCell>
                            {editMode && <TableCell />}
                          </TableRow>
                        </>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Notes Dialog */}
      <Dialog open={notesDialogOpen} onClose={handleCloseNotesDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Purchase Order Notes</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes for this purchase order..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNotesDialog}>Cancel</Button>
          <Button onClick={handleSaveNotes} disabled={savingNotes} variant="contained">
            {savingNotes ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default PurchaseOrderDetail;
